import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/config';
import { isGuestTypeDisabled, validateGuestTypeSelection, allowedGuestTypes } from '../utils/guestTypeRules';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Badge } from './ui/badge';
import { 
  X, 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  UserPlus, 
  Send,
  Loader2,
  User,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Eye,
  CheckSquare,
  Square,
  Trash2,
  Lock,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from './ui/checkbox';
import ComuneAutocomplete from './ComuneAutocomplete';

// Lista di fallback (usata solo se il fetch della lista completa dal backend fallisce).
// La lista COMPLETA dei paesi (incl. Arabia Saudita, Emirati, Qatar, ecc.) viene
// caricata a runtime da GET /api/locations/countries/all (stessa fonte del check-in ospiti).
const COUNTRIES_FALLBACK = [
  'Italia', 'Albania', 'Algeria', 'Arabia Saudita', 'Argentina', 'Australia', 'Austria',
  'Bahrain', 'Belgio', 'Bosnia ed Erzegovina', 'Brasile', 'Bulgaria', 'Canada',
  'Cina', 'Cipro', 'Colombia', 'Corea del Sud', 'Croazia', 'Danimarca',
  'Ecuador', 'Egitto', 'Emirati Arabi Uniti', 'Estonia', 'Filippine', 'Finlandia', 'Francia',
  'Germania', 'Giappone', 'Giordania', 'Grecia', 'India', 'Indonesia', 'Iran', 'Iraq', 'Irlanda',
  'Israele', 'Kosovo', 'Kuwait', 'Libano', 'Lettonia', 'Lituania', 'Lussemburgo', 'Macedonia del Nord',
  'Malta', 'Marocco', 'Messico', 'Moldavia', 'Montenegro', 'Nigeria',
  'Norvegia', 'Oman', 'Paesi Bassi', 'Pakistan', 'Perù', 'Polonia', 'Portogallo', 'Qatar',
  'Regno Unito', 'Repubblica Ceca', 'Romania', 'Russia', 'Serbia',
  'Slovacchia', 'Slovenia', 'Spagna', 'Sri Lanka', 'Stati Uniti',
  'Sudafrica', 'Svezia', 'Svizzera', 'Thailandia', 'Tunisia', 'Turchia',
  'Ucraina', 'Ungheria', 'Venezuela', 'Vietnam', 'Altro'
];

// Mappa cittadinanza (valori del menu) -> Stato di residenza (nome paese in COUNTRIES).
// Default intelligente: per i turisti la residenza coincide quasi sempre con la cittadinanza.
// Evita che la residenza resti "Italia" per uno straniero (che farebbe scattare l'obbligo
// di Comune/Provincia italiani richiesto a ROSS1000/Alloggiati solo per i residenti in Italia).
const CITIZENSHIP_TO_COUNTRY = {
  'Italiana': 'Italia', 'Francese': 'Francia', 'Tedesca': 'Germania', 'Spagnola': 'Spagna',
  'Britannica': 'Regno Unito', 'Americana': 'Stati Uniti', 'Svizzera': 'Svizzera',
  'Austriaca': 'Austria', 'Belga': 'Belgio', 'Olandese': 'Paesi Bassi', 'Portoghese': 'Portogallo',
  'Polacca': 'Polonia', 'Rumena': 'Romania', 'Greca': 'Grecia', 'Svedese': 'Svezia',
  'Norvegese': 'Norvegia', 'Danese': 'Danimarca', 'Finlandese': 'Finlandia', 'Irlandese': 'Irlanda',
  'Ceca': 'Repubblica Ceca', 'Ungherese': 'Ungheria', 'Croata': 'Croazia', 'Slovena': 'Slovenia',
  'Russa': 'Russia', 'Ucraina': 'Ucraina', 'Turca': 'Turchia', 'Marocchina': 'Marocco',
  'Tunisina': 'Tunisia', 'Egiziana': 'Egitto', 'Brasiliana': 'Brasile', 'Argentina': 'Argentina',
  'Messicana': 'Messico', 'Canadese': 'Canada', 'Australiana': 'Australia', 'Giapponese': 'Giappone',
  'Cinese': 'Cina', 'Indiana': 'India',
};

// Applica la cittadinanza a un oggetto ospite derivando lo Stato di residenza (se mappato).
// Se la residenza diventa estera, azzera la provincia italiana (non valida all'estero).
// Allinea anche il Paese di Nascita quando è ancora al default 'Italia' e la cittadinanza
// è estera (evita che per uno straniero la nascita resti erroneamente "Italia").
const applyCitizenshipToGuest = (guest, citizenship, countriesList = COUNTRIES_FALLBACK) => {
  const next = { ...guest, citizenship };
  let derived = CITIZENSHIP_TO_COUNTRY[citizenship];
  // La cittadinanza può arrivare già come NOME PAESE (es. dati importati: "ARABIA SAUDITA").
  if (!derived && citizenship) {
    const norm = String(citizenship).trim().toLowerCase();
    const match = (countriesList || []).find((c) => c.toLowerCase() === norm);
    if (match) derived = match;
  }
  if (derived) {
    next.residence_country = derived;
    if (derived !== 'Italia') next.residence_province = '';
    // Default intelligente sulla nascita: solo se non è stata scelta esplicitamente
    // (cioè vuota o ancora il default 'Italia'). L'operatore può sempre modificarla.
    if (derived !== 'Italia' && (!guest.birth_country || guest.birth_country === 'Italia')) {
      next.birth_country = derived;
      next.birth_province = '';
    }
  }
  return next;
};

// Tipi "leader" della schedina Alloggiati: per loro il documento è SEMPRE obbligatorio.
const LEADER_GUEST_TYPES = ['singolo', 'single', 'capofamiglia', 'capo_famiglia', 'head_of_family', 'capogruppo', 'group_leader'];

// Messaggio (in italiano) per le violazioni delle regole sulla tipologia ospite.
const guestTypeErrorMessage = (code) => {
  if (code === 'must_be_single') return 'Con un solo ospite la tipologia deve essere "Singolo".';
  if (code === 'single_not_allowed') return 'Con più ospiti non è ammessa la tipologia "Singolo": indica un Capofamiglia/Capogruppo e gli altri come familiari/membri.';
  if (code === 'multiple_heads') return 'Può esserci un solo Capofamiglia o Capogruppo per prenotazione. Imposta gli altri ospiti come familiari o membri del gruppo.';
  if (code === 'no_head') return 'Con più ospiti deve esserci almeno un Capofamiglia o Capogruppo.';
  return 'Tipologia ospite non valida.';
};

const guestAgeYears = (birthDate) => {
  if (!birthDate) return null;
  const bd = new Date(birthDate);
  if (isNaN(bd.getTime())) return null;
  return (Date.now() - bd.getTime()) / (365.25 * 24 * 3600 * 1000);
};

// Il documento è obbligatorio per i leader (sempre) e per ogni ospite >=14 anni
// (allineato alla validazione backend validate_guest_for_questura).
const isDocumentRequired = (g) => {
  if (!g) return true;
  const type = (g.guest_type || 'singolo').toLowerCase();
  if (LEADER_GUEST_TYPES.includes(type)) return true;
  const age = guestAgeYears(g.birth_date);
  return age === null || age >= 14;
};

// Ritorna l'elenco dei campi documento mancanti (se obbligatori), altrimenti [].
const missingDocumentFields = (g) => {
  if (!isDocumentRequired(g)) return [];
  const missing = [];
  if (!g.document_type) missing.push('Tipo Documento');
  if (!g.document_number) missing.push('Numero Documento');
  if (!g.document_issue_place && !g.document_issue_country) missing.push('Luogo/Paese di Rilascio');
  return missing;
};



// --- Rilevamento ospiti duplicati ---
const _normName = (s) => (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
const _normDocNum = (s) => (s || '').toString().replace(/[\s.\-/]/g, '').toUpperCase();
// Trova un ospite già presente che coincide col candidato:
// match SOLO se coincidono TUTTI E 4 i dati: nome + cognome + data di nascita + numero documento.
const findDuplicateGuest = (candidate, list, excludeId = null) => {
  const cDoc = _normDocNum(candidate.document_number);
  const cFn = _normName(candidate.first_name);
  const cLn = _normName(candidate.last_name);
  const cBd = (candidate.birth_date || '').toString().trim();
  // Tutti e 4 i campi devono essere valorizzati per poter parlare di duplicato
  if (!cDoc || !cFn || !cLn || !cBd) return null;
  return (list || []).find((g) => {
    if (excludeId && g.id === excludeId) return false;
    return (
      cDoc === _normDocNum(g.document_number) &&
      cFn === _normName(g.first_name) &&
      cLn === _normName(g.last_name) &&
      cBd === (g.birth_date || '').toString().trim()
    );
  }) || null;
};

// Validazione date: data di nascita e data di rilascio del documento NON possono
// essere nel futuro (la scadenza sì). Ritorna il messaggio d'errore (IT) o '' se ok.
const futureDateError = (g) => {
  if (!g) return '';
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const bd = (g.birth_date || '').toString().slice(0, 10);
  const issue = (g.document_issue_date || '').toString().slice(0, 10);
  if (bd && bd > today) {
    return '⚠️ Data non valida: la data di nascita non può essere futura. Controlla e correggi.';
  }
  if (issue && issue > today) {
    return '⚠️ Data non valida: la data di rilascio del documento non può essere futura. Controlla e correggi.';
  }
  return '';
};

// Campi obbligatori della scheda ospite (marcati con ✱ nel form).
// I dati documento sono obbligatori solo per leader/maggiori di 14 anni (vedi missingDocumentFields).
const REQUIRED_PERSONAL_FIELDS = [
  ['first_name', 'Nome'],
  ['last_name', 'Cognome'],
  ['gender', 'Sesso'],
  ['guest_type', 'Tipo Ospite'],
  ['birth_date', 'Data di nascita'],
  ['birth_place', 'Luogo di nascita'],
  ['birth_country', 'Paese di nascita'],
  ['citizenship', 'Cittadinanza'],
];
// Provincia di nascita: obbligatoria quando il Paese di nascita è l'Italia.
const isItalianBirth = (g) => {
  const bc = (g?.birth_country || '').toString().trim().toLowerCase();
  return ['italia', 'italy', 'it'].includes(bc);
};

// Rileva un nome di stato estero nel testo del luogo di nascita
// (es. "SAN FRANCISCO (CORDOBA, ARGENTINA)" con Paese di nascita "Italia" = dato incoerente).
const FOREIGN_HINT_ALIASES = ['ARG', 'ARGENTINA', 'TAIWAN', 'BRASILE', 'BRAZIL', 'USA', 'URUGUAY',
  'VENEZUELA', 'CILE', 'CHILE', 'PERU', 'MESSICO', 'MEXICO', 'COLOMBIA', 'ENGLAND', 'FRANCE', 'GERMANY', 'SPAIN'];
const foreignCountryInPlace = (place) => {
  const p = ` ${String(place || '').toUpperCase().replace(/[(),.']/g, ' ').replace(/\s+/g, ' ')} `;
  if (!p.trim()) return '';
  const names = [...new Set([...COUNTRIES_FALLBACK.filter(c => c !== 'Italia' && c !== 'Altro'), ...FOREIGN_HINT_ALIASES])];
  for (const name of names) {
    const n = name.toUpperCase();
    if (n.length >= 3 && p.includes(` ${n} `)) return name;
  }
  return '';
};

// Se il comune di nascita è riconosciuto (es. MILANO), la provincia si
// autocompila dalla banca dati comuni Polizia di Stato (match esatto sul nome).
const autofillBirthProvince = async (g) => {
  if (!g || !isItalianBirth(g)) return g;
  if ((g.birth_province || '').toString().trim()) return g;
  const place = (g.birth_place || '').toString().trim();
  if (!place) return g;
  try {
    const res = await axios.get(`${API}/api/questura/comuni/search`, { params: { q: place, limit: 10 } });
    const exact = (res.data?.results || []).find(
      (c) => (c.nome || '').trim().toUpperCase() === place.toUpperCase()
    );
    if (exact?.provincia) return { ...g, birth_province: exact.provincia };
  } catch (e) { /* lookup best-effort: la validazione bloccherà comunque */ }
  return g;
};

// Ritorna l'elenco ESATTO dei campi obbligatori mancanti per un ospite (personali + documento).
const missingRequiredFields = (g) => {
  if (!g) return ['tutti i dati'];
  const missing = [];
  for (const [key, label] of REQUIRED_PERSONAL_FIELDS) {
    if (!(g[key] || '').toString().trim()) missing.push(label);
  }
  // Provincia di nascita obbligatoria per i nati in Italia
  if (isItalianBirth(g) && !(g.birth_province || '').toString().trim()) {
    missing.push('Provincia di nascita');
  }
  // Incoerenza: Paese di nascita "Italia" ma il luogo contiene uno stato estero
  if (isItalianBirth(g)) {
    const hint = foreignCountryInPlace(g.birth_place);
    if (hint) missing.push(`Paese di nascita incoerente: il luogo "${g.birth_place}" sembra estero (${hint}) — correggi il Paese di nascita`);
  }
  missing.push(...missingDocumentFields(g));
  return missing;
};

// Estrae il motivo reale del fallimento Questura dalla risposta backend.
// Gli endpoint /alloggiati/submit e /submit-selected mettono il motivo in
// `message`/`errors` (non in `error`), per questo prima si vedeva solo il generico.
const extractQuesturaError = (data) => {
  if (!data) return '';
  const errsArr = Array.isArray(data.errors)
    ? data.errors.map((e) => (typeof e === 'string' ? e : (e?.message || e?.error || e?.field || JSON.stringify(e)))).filter(Boolean)
    : [];
  return (
    data.detail ||
    data.error ||
    (errsArr.length ? errsArr.join(' · ') : '') ||
    data.message ||
    ''
  );
};

// Trasforma il messaggio grezzo della Questura in un messaggio chiaro e azionabile.
const mapQuesturaError = (rawDetail) => {
  const errDetail = rawDetail || '';
  if (errDetail.includes('ERRORE_PARAMETRO_ERRATO')) {
    return 'Errore parametro: controlla che tutti i campi siano compilati correttamente (nomi, date, codici). Verifica ogni ospite singolarmente.';
  }
  if (errDetail.includes('GestioneAppartamenti') || errDetail.includes('metodi appropriati')) {
    return "Errore tipo struttura: l'account Questura è di tipo 'Gestore Appartamenti'. Verifica che l'ID Appartamento sia configurato nella scheda immobile (Configurazione Immobili > Configura).";
  }
  if (errDetail.includes('credenziali') || errDetail.includes('wskey') || errDetail.includes('401')) {
    return 'Errore credenziali Questura: la chiave WS (wskey) non è valida o è scaduta. Aggiorna le credenziali nella configurazione immobile.';
  }
  if (errDetail.includes('timeout') || errDetail.includes('connection')) {
    return 'Il server della Questura non risponde. Riprova tra qualche minuto.';
  }
  return errDetail || "Errore imprevisto nell'invio alla Questura. Contatta l'amministratore.";
};

// Riga leggibile per un singolo errore Questura (pre-validazione o portale arricchito).
const formatQuesturaErrorLine = (e) => {
  if (typeof e === 'string') return [e];
  if (!e || typeof e !== 'object') return [];
  const out = [];
  const reasonIt = e.reason_it || e.detail || e.description || e.message || e.error || `Errore ${e.code || ''}`;
  if (e.guest && e.field) {
    out.push(`\u{1F464} ${e.guest} \u2014 ${e.field}: ${e.error || 'campo mancante'}`);
  } else if (e.guest) {
    out.push(`\u{1F464} ${e.guest} \u2014 ${reasonIt}`);
    if (e.reason_translated && e.reason_translated.trim() && e.reason_translated !== reasonIt) {
      out.push(`\u{1F310} ${e.reason_lang || 'lingua ospite'}: ${e.reason_translated}`);
    }
  } else {
    out.push(reasonIt);
  }
  return out;
};

// Classifica un errore Questura per capire quale campo della scheda va corretto
const classifyQuesturaError = (text) => {
  const t = (text || '').toLowerCase();
  if (t.includes('comune di nascita') || t.includes('luogo di nascita')) return 'birth';
  if (t.includes('luogo rilascio') || t.includes('luogo di rilascio')) return 'issue';
  if (t.includes('capo gruppo') || t.includes('capo famiglia') || t.includes('capogruppo') || t.includes('capofamiglia')) return 'capo';
  if (t.includes('data di nascita')) return 'birth_date';
  if (t.includes('cittadinanza')) return 'citizenship';
  if (t.includes('documento')) return 'document';
  return 'generic';
};

// Estrae ospite+motivo dai messaggi di pre-validazione ("Ospite 'NOME': motivo...")
const parseGuestFromString = (s) => {
  const m = /^Ospite '([^']+)'\s*:\s*(.*)$/.exec(String(s || ''));
  return m ? { guest: m[1], reason: m[2] } : null;
};

// Nomi canonici (italiano) per gli alias rilevati nel testo dei luoghi
const CANON_COUNTRY = { ARG: 'Argentina', BRAZIL: 'Brasile', USA: 'Stati Uniti', CHILE: 'Cile', MEXICO: 'Messico', ENGLAND: 'Regno Unito', FRANCE: 'Francia', GERMANY: 'Germania', SPAIN: 'Spagna', PERU: 'Perù' };
const canonCountry = (n) => CANON_COUNTRY[String(n || '').toUpperCase()] || n;

// Costruisce i dettagli del pannello errore dall'esito dell'invio Questura (tutte le forme note).
const buildSendErrorDetails = (data) => {
  const d = data || {};
  const items = [];
  (Array.isArray(d.errors) ? d.errors : []).forEach((e) => items.push(...formatQuesturaErrorLine(e)));
  (Array.isArray(d.validation_errors) ? d.validation_errors : []).forEach((e) => items.push(...formatQuesturaErrorLine(e)));
  // Voci strutturate per la CORREZIONE GUIDATA (ospite → campo da correggere)
  const entries = [];
  (Array.isArray(d.errors) ? d.errors : []).forEach((e) => {
    if (e && typeof e === 'object' && e.guest) {
      const reasonIt = e.reason_it || e.detail || e.description || e.message || e.error || `Errore ${e.code || ''}`;
      entries.push({
        guest: e.guest,
        reason: e.field ? `${e.field}: ${e.error || reasonIt}` : reasonIt,
        kind: classifyQuesturaError(`${e.field || ''} ${reasonIt}`),
        translated: e.reason_translated, lang: e.reason_lang,
      });
    }
  });
  (Array.isArray(d.validation_errors) ? d.validation_errors : []).forEach((e) => {
    if (typeof e === 'string') {
      const p = parseGuestFromString(e);
      if (p) entries.push({ guest: p.guest, reason: p.reason, kind: classifyQuesturaError(p.reason) });
    } else if (e && typeof e === 'object' && e.guest) {
      const reason = e.field ? `${e.field}: ${e.error || ''}` : (e.reason_it || e.error || '');
      entries.push({ guest: e.guest, reason, kind: classifyQuesturaError(`${e.field || ''} ${reason}`) });
    }
  });
  let title = 'Invio alla Questura non riuscito';
  let hint = '';
  if (d.error_code === 'INVALID_CREDENTIALS') {
    title = 'Credenziali Questura ERRATE';
    hint = 'Il portale AlloggiatiWeb ha rifiutato le credenziali. Correggile dal pulsante Questura in "Arrivi e Partenze" (o nella scheda immobile) e riprova: verranno verificate subito sul portale.';
  } else if (d.error_code === 'MISSING_CREDENTIALS') {
    title = 'Credenziali Questura mancanti';
    hint = "L'ufficio \u00e8 stato avvisato automaticamente via email. Configura username, password e WSKey nella scheda immobile.";
  } else if (d.error_code === 'MISSING_GUEST_FIELDS' || d.error_code === 'NO_GUESTS') {
    title = 'Anagrafiche ospiti incomplete';
    hint = 'Apri "Modifica" sull\'ospite indicato, completa i campi elencati e riprova.';
  } else if (d.error_code === 'ARRIVAL_IN_FUTURE' || d.error_code === 'ARRIVAL_OUT_OF_WINDOW') {
    title = 'Fuori finestra di invio Questura';
  } else if (items.length) {
    hint = 'Correggi i dati indicati (pulsante "Modifica" sull\'ospite) e riprova.';
  }
  const message = d.detail || d.error || d.message ||
    (items.length ? '' : "Errore imprevisto nell'invio alla Questura. Riprova o contatta l'amministrazione.");
  return { title, message, items, hint, entries };
};



const GuestVerificationModal = ({ booking, onClose, onVerified, allowTurnoverToggle = false }) => {
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  // REMOVED: childrenCount - minori ora inseriti come ospiti normali con is_minor calcolato dal backend
  const [currentGuestIndex, setCurrentGuestIndex] = useState(0);
  const [editedGuest, setEditedGuest] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingQuestura, setSendingQuestura] = useState(false);
  const [sendError, setSendError] = useState(null); // dettagli errore invio Questura (pannello)
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [newGuest, setNewGuest] = useState(getEmptyGuest());
  const [verifiedGuests, setVerifiedGuests] = useState(new Set());
  const [showFinalConfirm, setShowFinalConfirm] = useState(false);
  const [localGuestsCount, setLocalGuestsCount] = useState(booking.guests_count || 1);
  const [updatingGuestsCount, setUpdatingGuestsCount] = useState(false);
  // New state for selective submission
  const [selectedGuestIds, setSelectedGuestIds] = useState(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showResidenceAdd, setShowResidenceAdd] = useState(false);
  const [showResidenceEdit, setShowResidenceEdit] = useState(false);
  // Lista paesi completa caricata dal backend (incl. Arabia Saudita, ecc.)
  const [dynamicCountries, setDynamicCountries] = useState(null);
  const countries = (dynamicCountries && dynamicCountries.length) ? dynamicCountries : COUNTRIES_FALLBACK;

  // AVVICENDAMENTO OSPITI: finestre di soggiorno per-ospite
  const [turnoverEnabled, setTurnoverEnabled] = useState(!!booking.guest_turnover_enabled);
  const [togglingTurnover, setTogglingTurnover] = useState(false);
  const [sendingTurnoverIstat, setSendingTurnoverIstat] = useState(false);
  const bookingCheckIn = (booking.check_in || '').toString().slice(0, 10);
  const bookingCheckOut = (booking.check_out || '').toString().slice(0, 10);

  const handleToggleTurnover = async (enabled) => {
    setTogglingTurnover(true);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.patch(
        `${API}/api/bookings/${booking.id}/guest-turnover`,
        { enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTurnoverEnabled(enabled);
      booking.guest_turnover_enabled = enabled; // riflette nel prop locale
      toast.success(enabled
        ? 'Avvicendamento ospiti ATTIVO: ogni ospite può avere date di soggiorno proprie.'
        : 'Avvicendamento ospiti disattivato.');
      if (onVerified) onVerified();
    } catch (error) {
      toast.error('Errore: ' + (error.response?.data?.detail || 'impossibile aggiornare'));
    } finally {
      setTogglingTurnover(false);
    }
  };

  const handleSendTurnoverIstat = async () => {
    setSendingTurnoverIstat(true);
    try {
      const token = localStorage.getItem('hw_token');
      const res = await axios.post(
        `${API}/api/istat/turnover/submit/${booking.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invio ISTAT avvicendamento completato: ${res.data?.dates_processed || 0} date elaborate.`);
    } catch (error) {
      toast.error('Errore invio ISTAT: ' + (error.response?.data?.detail || 'errore sconosciuto'));
    } finally {
      setSendingTurnoverIstat(false);
    }
  };

  // Toggle "Avvicendamento ospiti" — riutilizzabile in tutte le viste del modale
  // (vista principale, stato "Nessun Documento", form Aggiungi Ospite) così l'ufficio
  // può abilitarlo anche PRIMA che esista il primo documento.
  const renderTurnoverToggle = () => {
    if (!allowTurnoverToggle) return null;
    return (
      <div className="mb-3 p-3 rounded-lg border border-amber-200 bg-amber-50 text-left" data-testid="turnover-section">
        <div className="flex items-start gap-2">
          <Checkbox
            id="turnover-toggle"
            checked={turnoverEnabled}
            disabled={togglingTurnover || isBookingLocked}
            onCheckedChange={(v) => handleToggleTurnover(!!v)}
            data-testid="turnover-toggle"
          />
          <div className="flex-1">
            <label htmlFor="turnover-toggle" className="text-sm font-semibold text-amber-800 cursor-pointer">
              Avvicendamento ospiti (date di soggiorno per-ospite)
            </label>
            <p className="text-xs text-amber-700">Attiva per prenotazioni dove un ospite esce prima e un altro subentra. Ogni ospite avrà date proprie e invii Questura/ISTAT precisi.</p>
          </div>
        </div>
        {turnoverEnabled && (
          <Button variant="outline" size="sm" className="mt-2 text-xs" onClick={handleSendTurnoverIstat} disabled={sendingTurnoverIstat} data-testid="send-turnover-istat-btn">
            {sendingTurnoverIstat ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
            Invia ora a ISTAT (date per-ospite)
          </Button>
        )}
      </div>
    );
  };

  // Calcola se abbiamo tutti gli ospiti (tutti i documenti = totale ospiti)
  const totalGuestsRequired = localGuestsCount;
  const documentsCount = guests.length;
  const allGuestsAccounted = documentsCount >= totalGuestsRequired;
  const canSendQuestura = allGuestsAccounted && documentsCount > 0 && guests.every(
    guest => guest.status === 'verified' || guest.status === 'sent_questura'
  );
  const reservedChildren = Number(booking.children_under_10 ?? booking.children_count ?? 0) || 0;
  const bookedPayingGuests = Math.max(1, totalGuestsRequired - reservedChildren);
  const registeredPayingGuests = guests.filter(guest => {
    const age = guestAgeYears(guest.birth_date);
    return age !== null && age >= 10;
  }).length;
  const ageToVerify = guests.filter(guest => guestAgeYears(guest.birth_date) === null).map(guest => `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || 'Ospite');
  const managerCapacityAlert = registeredPayingGuests > bookedPayingGuests;

  // Check if Questura deadline has passed
  const isQuesturaDeadlineExpired = (() => {
    if (booking.questura_sent) return false; // already sent, not expired
    const checkIn = booking.check_in;
    if (!checkIn) return false;
    const dateStr = checkIn.includes('T') ? checkIn.split('T')[0] : checkIn;
    const [year, month, day] = dateStr.split('-').map(Number);
    // Deadline: 23:59 del giorno successivo al check-in
    const deadline = new Date(year, month - 1, day + 2, 0, 0); // midnight after deadline day
    return new Date() > deadline;
  })();

  // Funzione per aggiornare il numero di ospiti con notifica email
  const handleUpdateGuestsCount = async (newCount) => {
    if (newCount < 1) return;
    
    setLocalGuestsCount(newCount);
    
    // Notifica al backend (con debounce)
    setUpdatingGuestsCount(true);
    try {
      const token = localStorage.getItem('hw_token');
      await axios.put(
        `${API}/api/responsible/booking/${booking.id}/update-guests`,
        {
          guests_count: newCount,
          note: `Modifica numero ospiti da ${booking.guests_count} a ${newCount}`
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Numero ospiti aggiornato. Notifica inviata all\'ufficio.');
    } catch (error) {
      console.error('Error updating guests count:', error);
      // Non mostriamo errore se non siamo responsabili
      if (error.response?.status !== 403) {
        toast.error('Errore aggiornamento: ' + (error.response?.data?.detail || 'Errore sconosciuto'));
      }
    } finally {
      setUpdatingGuestsCount(false);
    }
  };

  function getEmptyGuest() {
    return {
      first_name: '',
      last_name: '',
      gender: '',
      guest_type: 'family_member',
      birth_date: '',
      birth_place: '',
      birth_province: '',
      birth_country: 'Italia',
      citizenship: '',
      codice_fiscale: '',
      residence_address: '',
      residence_city: '',
      residence_province: '',
      residence_postal_code: '',
      residence_country: 'Italia',
      document_type: 'id_card',
      document_number: '',
      document_issue_country: 'Italia',
      document_issue_place: '',
      document_issue_date: '',
      document_expiry_date: '',
      arrival_date: '',
      departure_date: ''
    };
  }

  useEffect(() => {
    fetchGuestDocuments();
  }, [booking.id]);

  // Carica la lista completa dei paesi dal backend (fonte unica, allineata al check-in ospiti)
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const r = await axios.get(`${API}/api/locations/countries/all`);
        if (r.data?.countries?.length) {
          const list = r.data.countries;
          // Metti 'Italia' in cima per comodità, il resto resta ordinato
          setDynamicCountries(['Italia', ...list.filter((c) => c !== 'Italia')]);
        }
      } catch (e) {
        console.error('Errore caricamento lista paesi:', e);
      }
    };
    loadCountries();
  }, []);

  // Quando si apre il form "Aggiungi Ospite", imposta una tipologia coerente con le
  // regole (1 ospite -> "Singolo"; più ospiti -> familiare/membro, capo gestito a parte).
  useEffect(() => {
    if (!showAddGuest) return;
    const total = Math.max(totalGuestsRequired || 1, guests.length + 1);
    const others = guests.map(g => g.guest_type);
    setNewGuest(prev => {
      const allowed = allowedGuestTypes(total, others);
      return allowed.includes(prev.guest_type) ? prev : { ...prev, guest_type: allowed[0] };
    });
  }, [showAddGuest, totalGuestsRequired, guests]);

  const fetchGuestDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/guest-documents/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter documents for this booking
      const bookingDocs = (response.data || []).filter(doc => doc.booking_id === booking.id);
      setGuests(bookingDocs);
      
      if (bookingDocs.length > 0) {
        setEditedGuest({ ...bookingDocs[0] });
        // Auto-expand residence if data already exists
        const doc = bookingDocs[0];
        if (doc.residence_city || doc.residence_address || doc.residence_province) {
          setShowResidenceEdit(true);
        }
      }
    } catch (error) {
      console.error('Error fetching guest documents:', error);
      toast.error('Errore nel caricamento dei documenti');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toISOString().split('T')[0];
    } catch {
      return dateStr;
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT');
    } catch {
      return dateStr;
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedGuest(prev => {
      if (field === 'citizenship') {
        // Deriva lo Stato di residenza dalla cittadinanza (default intelligente per turisti)
        return applyCitizenshipToGuest(prev, value, countries);
      }
      return { ...prev, [field]: value };
    });
  };

  const handleSaveGuest = async () => {
    if (!editedGuest) return;

    // Autocompila la provincia di nascita se il comune è riconosciuto (es. MILANO -> MI)
    const guestToSave = await autofillBirthProvince(editedGuest);
    if (guestToSave !== editedGuest) setEditedGuest(guestToSave);

    // Validazione campi obbligatori (personali + documento): blocca e mostra l'elenco
    // ESATTO dei campi mancanti per QUESTO ospite, così si interviene subito.
    const missEdit = missingRequiredFields(guestToSave);
    if (missEdit.length) {
      toast.error(`⚠️ Campi obbligatori mancanti per questo ospite: ${missEdit.join(', ')}. Completa la scheda prima di salvare.`);
      return;
    }
    // Controllo ospite duplicato (esclude se stesso): stesso nome + cognome + data nascita + n° documento
    const dupEdit = findDuplicateGuest(guestToSave, guests, guestToSave.id);
    if (dupEdit) {
      toast.error(`Ospite duplicato: ${dupEdit.first_name || ''} ${dupEdit.last_name || ''} risulta già presente con gli stessi dati (nome, cognome, data di nascita e numero documento). Modifica i dati per renderli univoci.`);
      return;
    }
    // Date non valide: data di nascita e data di rilascio NON possono essere nel futuro
    if (futureDateError(guestToSave)) {
      toast.error(futureDateError(guestToSave));
      return;
    }
    // Regola tipologia ospite (1 ospite -> Singolo; più ospiti -> un solo capo)
    const _gtErrEdit = validateGuestTypeSelection(
      guestToSave.guest_type,
      Math.max(totalGuestsRequired || 1, guests.length),
      guests.filter(g => g.id !== guestToSave.id).map(g => g.guest_type)
    );
    if (_gtErrEdit) {
      toast.error(guestTypeErrorMessage(_gtErrEdit));
      return;
    }
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('hw_token');
      
      await axios.put(
        `${API}/api/guest-documents/${guestToSave.id}`,
        guestToSave,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Dati salvati correttamente');
      // Aggiorna lo stato locale senza chiudere il modale (resta aperto per gli altri ospiti)
      setGuests(prev => prev.map(g => (g.id === guestToSave.id ? { ...g, ...guestToSave } : g)));
      setIsEditing(false);
      if (onVerified) onVerified();
    } catch (error) {
      console.error('Error saving guest:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmGuest = async () => {
    const current = guests[currentGuestIndex];
    if (!current) return;

    let currentToVerify = current;
    if (isItalianBirth(current) && !(current.birth_province || '').toString().trim()) {
      const filled = await autofillBirthProvince(current);
      if ((filled.birth_province || '').toString().trim()) {
        currentToVerify = filled;
        toast.success(`Provincia di nascita compilata automaticamente: ${filled.birth_province}`);
      }
    }

    const missing = missingRequiredFields(currentToVerify);
    if (missing.length) {
      toast.error(`⚠️ Campi obbligatori mancanti per questo ospite: ${missing.join(', ')}. Completa la scheda prima di proseguire.`);
      setEditedGuest({ ...currentToVerify });
      setIsEditing(true);
      return;
    }
    const duplicate = findDuplicateGuest(currentToVerify, guests, current.id);
    if (duplicate) {
      toast.error(`Ospite duplicato: ${duplicate.first_name || ''} ${duplicate.last_name || ''} risulta già presente con gli stessi dati.`);
      setEditedGuest({ ...current });
      setIsEditing(true);
      return;
    }
    const dateError = futureDateError(currentToVerify);
    if (dateError) {
      toast.error(dateError);
      setEditedGuest({ ...current });
      setIsEditing(true);
      return;
    }
    const guestTypeError = validateGuestTypeSelection(
      currentToVerify.guest_type,
      Math.max(totalGuestsRequired || 1, guests.length),
      guests.filter(guest => guest.id !== current.id).map(guest => guest.guest_type)
    );
    if (guestTypeError) {
      toast.error(guestTypeErrorMessage(guestTypeError));
      setEditedGuest({ ...current });
      setIsEditing(true);
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('hw_token');
      await axios.put(
        `${API}/api/guest-documents/${current.id}`,
        { ...currentToVerify, status: 'verified', verified_at: new Date().toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedGuests = guests.map(guest => (
        guest.id === current.id ? { ...guest, ...currentToVerify, status: 'verified', verified_at: new Date().toISOString() } : guest
      ));
      setGuests(updatedGuests);
      setVerifiedGuests(prev => new Set([...prev, current.id]));

      if (currentGuestIndex < updatedGuests.length - 1) {
        const nextIndex = currentGuestIndex + 1;
        setCurrentGuestIndex(nextIndex);
        setEditedGuest({ ...updatedGuests[nextIndex] });
        setIsEditing(false);
      } else {
        setShowFinalConfirm(true);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const fields = detail?.missing_fields;
      toast.error(fields?.length
        ? `Campi obbligatori mancanti: ${fields.join(', ')}`
        : (detail?.message || detail || 'Errore durante la verifica dell’ospite'));
      setEditedGuest({ ...current });
      setIsEditing(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrevGuest = () => {
    if (currentGuestIndex > 0) {
      const prevIndex = currentGuestIndex - 1;
      setCurrentGuestIndex(prevIndex);
      setEditedGuest({ ...guests[prevIndex] });
      setIsEditing(false);
    }
  };

  const handleAddGuest = async () => {
    // Autocompila la provincia di nascita se il comune è riconosciuto (es. MILANO -> MI)
    const guestToAdd = await autofillBirthProvince(newGuest);
    if (guestToAdd !== newGuest) setNewGuest(guestToAdd);
    // Validazione campi obbligatori (personali + documento): blocca e mostra l'elenco
    // ESATTO dei campi mancanti per QUESTO ospite, prima di passare al successivo.
    const missAdd = missingRequiredFields(guestToAdd);
    if (missAdd.length) {
      toast.error(`⚠️ Campi obbligatori mancanti per questo ospite: ${missAdd.join(', ')}. Completa la scheda prima di proseguire.`);
      return;
    }
    // Controllo ospite duplicato (stesso nome + cognome + data nascita + n° documento)
    const dup = findDuplicateGuest(guestToAdd, guests);
    if (dup) {
      toast.error(`Ospite duplicato: ${dup.first_name || ''} ${dup.last_name || ''} risulta già inserito con gli stessi dati (nome, cognome, data di nascita e numero documento). Verifica prima di aggiungerlo di nuovo.`);
      return;
    }
    // Date non valide: data di nascita e data di rilascio NON possono essere nel futuro
    if (futureDateError(guestToAdd)) {
      toast.error(futureDateError(guestToAdd));
      return;
    }
    // Regola tipologia ospite (1 ospite -> Singolo; più ospiti -> un solo capo)
    const _gtErrAdd = validateGuestTypeSelection(
      guestToAdd.guest_type,
      Math.max(totalGuestsRequired || 1, guests.length + 1),
      guests.map(g => g.guest_type)
    );
    if (_gtErrAdd) {
      toast.error(guestTypeErrorMessage(_gtErrAdd));
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('hw_token');
      
      const guestData = {
        ...guestToAdd,
        booking_id: booking.id,
        property_id: booking.property_id,
        guest_index: guests.length + 1,
        is_main_guest: false,
        status: 'submitted'
      };
      
      const response = await axios.post(
        `${API}/api/guest-documents`,
        guestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.guest_capacity_alert?.is_over_capacity) {
        toast.warning('Gli ospiti paganti inseriti superano quelli prenotati. Prenotazioni è stata avvisata.');
      }
      
      // Add to local state
      const addedGuest = response.data;
      const updatedGuests = [...guests, addedGuest];
      setGuests(updatedGuests);
      setNewGuest(getEmptyGuest());
      if (onVerified) onVerified(); // aggiorna i conteggi del parent (NON chiude il modale)

      const remaining = totalGuestsRequired - updatedGuests.length;
      if (remaining > 0) {
        // Restano ospiti da inserire: resta nel form "Aggiungi" e prepara automaticamente il prossimo
        toast.success(`Ospite ${updatedGuests.length}/${totalGuestsRequired} aggiunto. Compila il prossimo ospite.`);
        // showAddGuest resta true → nuovo form vuoto già pronto per il prossimo ospite
        if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Tutti gli ospiti inseriti → torna alla vista verifica (senza chiudere il modale)
        setShowAddGuest(false);
        setCurrentGuestIndex(0);
        setEditedGuest({ ...updatedGuests[0] });
        setIsEditing(false);
        toast.success(`Tutti gli ospiti inseriti (${updatedGuests.length}/${totalGuestsRequired})`);
      }
    } catch (error) {
      console.error('Error adding guest:', error);
      toast.error('Errore nell\'aggiunta dell\'ospite');
    } finally {
      setSubmitting(false);
    }
  };


  const handleDeleteGuest = async (guestId, guestName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il documento di ${guestName}?`)) return;
    try {
      const token = localStorage.getItem('hw_token');
      await axios.delete(`${API}/api/guest-documents/${guestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuests(prev => prev.filter(g => g.id !== guestId));
      setSelectedGuestIds(prev => { const n = new Set(prev); n.delete(guestId); return n; });
      if (currentGuestIndex >= guests.length - 1 && currentGuestIndex > 0) {
        setCurrentGuestIndex(currentGuestIndex - 1);
      }
      toast.success(`Documento di ${guestName} eliminato`);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Errore nell\'eliminazione';
      toast.error(detail);
    }
  };

  const isGuestLocked = (guest) => {
    // Se il booking è già inviato alla Questura, TUTTI gli ospiti sono bloccati
    if (booking.questura_sent) return true;
    return guest?.questura_sent_at || guest?.status === 'sent_questura';
  };
  
  // Indica se l'intero booking è cristallizzato (inviato alla Questura)
  const isBookingLocked = !!booking.questura_sent;

  const handleFinalSubmit = async () => {
    const missingVerification = guests.filter(
      guest => guest.status !== 'verified' && guest.status !== 'sent_questura'
    );
    if (!allGuestsAccounted || missingVerification.length > 0) {
      toast.error('Verifica tutti gli ospiti prima di completare il check-in.');
      setShowFinalConfirm(false);
      const firstMissing = guests.findIndex(guest => (
        guest.status !== 'verified' && guest.status !== 'sent_questura'
      ));
      if (firstMissing >= 0) {
        setCurrentGuestIndex(firstMissing);
        setEditedGuest({ ...guests[firstMissing] });
      }
      return;
    }
    toast.success('Ospiti salvati e verifica completata.');
    onVerified && onVerified();
    onClose();
  };

  // Invia alla Questura
  const handleSendQuestura = async () => {
    console.log('🚀 Modal handleSendQuestura chiamato');
    console.log('📊 canSendQuestura:', canSendQuestura);
    console.log('📄 documentsCount:', documentsCount, 'totalGuestsRequired:', totalGuestsRequired);
    
    if (!canSendQuestura) {
      toast.error(`Verifica tutti gli ospiti prima di inviare (${documentsCount}/${totalGuestsRequired})`);
      return;
    }

    try {
      setSendingQuestura(true);
      const token = localStorage.getItem('hw_token');
      console.log('🔑 Token presente:', !!token);
      console.log('📤 Chiamata API:', `${API}/api/alloggiati/submit/${booking.id}`);
      
      const response = await axios.post(
        `${API}/api/alloggiati/submit/${booking.id}`,
        {}, // No more children_count needed
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('📥 Risposta API:', response.data);
      
      if (response.data.success) {
        toast.success('Dati inviati alla Questura con successo!');
        onVerified && onVerified();
        onClose();
      } else {
        setSendError(buildSendErrorDetails(response.data));
      }
    } catch (error) {
      console.error('Error sending to Questura:', error);
      setSendError(buildSendErrorDetails(error.response?.data));
    } finally {
      setSendingQuestura(false);
    }
  };

  // Fetch preview schedine
  const handlePreviewSchedine = async () => {
    try {
      setLoadingPreview(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/api/alloggiati/preview-schedine/${booking.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.error) {
        toast.error(response.data.error);
        return;
      }
      
      setPreviewData(response.data);
      setShowPreview(true);
    } catch (error) {
      console.error('Error fetching preview:', error);
      toast.error(error.response?.data?.detail || 'Errore nel caricamento anteprima');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Toggle guest selection
  const toggleGuestSelection = (guestId) => {
    setSelectedGuestIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(guestId)) {
        newSet.delete(guestId);
      } else {
        newSet.add(guestId);
      }
      return newSet;
    });
  };

  // Select all guests
  const selectAllGuests = () => {
    const allIds = guests.filter(g => g.status === 'submitted' || g.status === 'verified').map(g => g.id);
    setSelectedGuestIds(new Set(allIds));
  };

  // Deselect all guests
  const deselectAllGuests = () => {
    setSelectedGuestIds(new Set());
  };

  // Send selected guests to Questura
  const handleSendSelectedQuestura = async () => {
    if (selectedGuestIds.size === 0) {
      toast.error('Seleziona almeno un ospite da inviare');
      return;
    }

    try {
      setSendingQuestura(true);
      const token = localStorage.getItem('hw_token');
      console.log('🚀 Invio selezionati alla Questura:', `${API}/api/alloggiati/submit-selected/${booking.id}`, Array.from(selectedGuestIds));
      const response = await axios.post(
        `${API}/api/alloggiati/submit-selected/${booking.id}`,
        { guest_ids: Array.from(selectedGuestIds) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('📥 Risposta submit-selected:', response.data);

      if (response.data.success) {
        toast.success(`${selectedGuestIds.size} ospite/i inviati alla Questura con successo!`);
        setSelectedGuestIds(new Set());
        onVerified && onVerified();
        // Don't close, allow more selections
      } else {
        // Il motivo reale del rifiuto arriva in message/errors (non in error)
        setSendError(buildSendErrorDetails(response.data));
      }
    } catch (error) {
      console.error('Error sending selected to Questura:', error);
      setSendError(buildSendErrorDetails(error.response?.data));
    } finally {
      setSendingQuestura(false);
    }
  };

  // ===== CORREZIONE GUIDATA: dal popup errori alla scheda giusta, già in modifica =====
  const [guidedFix, setGuidedFix] = useState(null);

  const computeGuidedSuggestion = (g, kind) => {
    if (kind === 'birth') {
      const hint = canonCountry(foreignCountryInPlace(g.birth_place));
      if (hint) return { field: 'birth_country', value: hint, label: `Nel luogo "${g.birth_place}" è indicato uno stato estero: imposta Paese di Nascita = ${hint}.` };
      return { field: 'birth_country', label: `Il luogo "${g.birth_place || '—'}" non risulta un comune italiano. Se l'ospite è nato all'estero imposta il Paese di Nascita corretto; se è nato in Italia correggi il nome del comune.` };
    }
    if (kind === 'issue') {
      const hint = canonCountry(foreignCountryInPlace(g.document_issue_place));
      if (hint) return { field: 'document_issue_country', value: hint, label: `Nel luogo di rilascio "${g.document_issue_place}" è indicato uno stato estero: imposta Paese Rilascio = ${hint}.` };
      return { field: 'document_issue_country', label: `Il luogo di rilascio "${g.document_issue_place || '—'}" non risulta un comune italiano. Se il documento è stato rilasciato all'estero (es. consolato o ambasciata) imposta il Paese di Rilascio corretto; se è italiano correggi il nome del comune.` };
    }
    if (kind === 'birth_date') return { field: null, label: 'Controlla la data di nascita (formato e coerenza con il documento).' };
    if (kind === 'citizenship') return { field: null, label: 'Controlla la cittadinanza indicata sul documento.' };
    if (kind === 'document') return { field: 'document_issue_country', label: 'Controlla tipo, numero e luogo di rilascio del documento.' };
    return { field: null, label: 'Controlla i dati della scheda e confrontali con il documento.' };
  };

  const startGuidedFix = (entry) => {
    const norm = (s) => String(s || '').toUpperCase().replace(/\s+/g, ' ').trim();
    let gi = guests.findIndex(g => norm(`${g.first_name} ${g.last_name}`) === norm(entry.guest));
    if (gi === -1) gi = guests.findIndex(g => norm(`${g.last_name} ${g.first_name}`) === norm(entry.guest));
    if (entry.kind === 'capo') {
      const ci = guests.findIndex(g => ['family_head', 'group_leader', 'capofamiglia', 'capogruppo'].includes(String(g.guest_type || '').toLowerCase()));
      if (ci !== -1) gi = ci;
    }
    if (gi === -1) { toast.error(`Scheda di ${entry.guest} non trovata`); return; }
    const g = guests[gi];
    if (isGuestLocked(g)) { toast.info('Questa scheda è già stata accettata dalla Questura (bloccata)'); return; }
    const sugg = entry.kind === 'capo'
      ? { field: null, label: "L'errore dei membri si risolve correggendo la scheda del CAPOGRUPPO, aperta qui sotto." }
      : computeGuidedSuggestion(g, entry.kind);
    setSendError(null);
    setCurrentGuestIndex(gi);
    setEditedGuest({ ...g });
    setIsEditing(true);
    setGuidedFix({ guestId: g.id, reason: entry.reason, ...sugg });
    setTimeout(() => {
      if (sugg.field) document.querySelector(`[data-guided-anchor="${sugg.field}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
  };

  const guidedCls = (f) => (guidedFix && guidedFix.field === f && guidedFix.guestId === currentGuest?.id
    ? 'ring-2 ring-amber-400 rounded-md bg-amber-50 px-1' : '');

  // Pannello dettagli errore invio Questura (mostra il motivo REALE, ospite per ospite)
  const plainErrorItems = sendError
    ? (sendError.entries?.length
        ? (sendError.items || []).filter(l => typeof l === 'string' && !l.startsWith('\u{1F464}') && !l.startsWith('\u{1F310}'))
        : (sendError.items || []))
    : [];
  const sendErrorOverlay = sendError ? (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-4" data-testid="questura-error-panel">
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[80vh] flex flex-col shadow-2xl">
        <div className="bg-red-600 text-white p-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-base font-bold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            {sendError.title}
          </h2>
          <Button variant="ghost" size="sm" onClick={() => setSendError(null)} className="text-white hover:bg-red-700" data-testid="close-questura-error-btn">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          {sendError.message && <p className="text-sm text-slate-800">{sendError.message}</p>}
          {sendError.entries?.length > 0 && (
            <div className="space-y-2" data-testid="questura-error-entries">
              {sendError.entries.map((en, i) => (
                <div key={i} className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm font-semibold text-red-800">{'\u{1F464}'} {en.guest}</p>
                  <p className="text-sm text-red-700 mt-0.5">{en.reason}</p>
                  {en.translated && <p className="text-xs text-slate-500 mt-0.5">{'\u{1F310}'} {en.lang || 'traduzione'}: {en.translated}</p>}
                  <Button size="sm" className="mt-2 h-8 bg-blue-600 hover:bg-blue-700 text-white" data-testid={`guided-fix-open-${i}`}
                    onClick={() => startGuidedFix(en)}>
                    {'\u{1F527}'} {en.kind === 'capo' ? 'Apri scheda capogruppo' : 'Correggi scheda'}
                  </Button>
                </div>
              ))}
            </div>
          )}
          {plainErrorItems.length > 0 && (
            <ul className="space-y-1.5 bg-red-50 border border-red-200 rounded-md p-3" data-testid="questura-error-items">
              {plainErrorItems.map((line, i) => (
                <li key={i} className="text-sm text-red-800 whitespace-pre-wrap">{line}</li>
              ))}
            </ul>
          )}
          {sendError.hint && <p className="text-xs text-slate-500">{sendError.hint}</p>}
        </div>
        <div className="p-4 border-t flex justify-end">
          <Button onClick={() => setSendError(null)} size="sm" data-testid="questura-error-ok-btn">Ho capito</Button>
        </div>
      </div>
    </div>
  ) : null;

  const currentGuest = guests[currentGuestIndex];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 flex flex-col items-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-4" />
          <p>Caricamento documenti...</p>
        </div>
      </div>
    );
  }

  // Preview Schedine Modal
  if (showPreview && previewData) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg w-full max-w-4xl my-8" data-testid="preview-schedine-modal">
          {sendErrorOverlay}
          <div className="bg-purple-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Anteprima Schedine Questura
            </h2>
            <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)} className="text-white hover:bg-purple-700">
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Booking Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Ospite:</span>
                  <p className="font-semibold">{previewData.guest_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Check-in:</span>
                  <p className="font-semibold">{previewData.check_in?.split('T')[0]}</p>
                </div>
                <div>
                  <span className="text-slate-500">Check-out:</span>
                  <p className="font-semibold">{previewData.check_out?.split('T')[0]}</p>
                </div>
                <div>
                  <span className="text-slate-500">Schedine:</span>
                  <p className="font-semibold">{previewData.schedine_count} / {previewData.guests_count}</p>
                </div>
              </div>
            </div>
            
            {/* Parsed Guest Records */}
            <div>
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Dettaglio Ospiti per Questura
              </h3>
              <div className="space-y-3">
                {previewData.parsed_guests?.map((guest, idx) => (
                  <div key={idx} className={`border rounded-lg p-4 ${guest.is_capo ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className={guest.is_capo ? 'bg-blue-600' : 'bg-slate-500'}>
                        {guest.is_capo ? 'Capogruppo' : 'Membro'}
                      </Badge>
                      <span className="font-semibold">{guest.guest_name}</span>
                      <Badge variant="outline" className="ml-auto">
                        Tipo: {guest.tipo_alloggiato}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Cognome:</span>
                        <p className="font-mono">{guest.cognome}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Nome:</span>
                        <p className="font-mono">{guest.nome}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Sesso:</span>
                        <p className="font-mono">{guest.sesso}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Data Nascita:</span>
                        <p className="font-mono">{guest.data_nascita}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Comune Nascita:</span>
                        <p className="font-mono">{guest.comune_nascita || '-'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Prov. Nascita:</span>
                        <p className="font-mono">{guest.provincia_nascita || '-'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Stato Nascita:</span>
                        <p className="font-mono">{guest.stato_nascita}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Cittadinanza:</span>
                        <p className="font-mono">{guest.cittadinanza}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Documento:</span>
                        <p className="font-mono">{guest.tipo_documento} - {guest.numero_documento}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Luogo Rilascio:</span>
                        <p className="font-mono">{guest.luogo_rilascio || '-'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Data Arrivo:</span>
                        <p className="font-mono">{guest.data_arrivo}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Giorni:</span>
                        <p className="font-mono">{guest.giorni_permanenza}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Raw Schedine */}
            {previewData.schedine_raw?.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2 text-sm text-slate-600">Dati Raw (168 caratteri)</h3>
                <div className="bg-slate-900 rounded-lg p-4 overflow-x-auto">
                  {previewData.schedine_raw.map((line, idx) => (
                    <div key={idx} className="font-mono text-xs text-green-400 whitespace-nowrap">
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="p-4 border-t flex gap-3">
            <Button 
              variant="outline" 
              onClick={() => setShowPreview(false)}
              className="flex-1"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Chiudi Anteprima
            </Button>
            <Button 
              onClick={() => {
                setShowPreview(false);
                handleSendQuestura();
              }}
              disabled={isBookingLocked || !canSendQuestura || sendingQuestura}
              className={`flex-1 ${(isBookingLocked || isQuesturaDeadlineExpired) ? 'hidden' : 'bg-purple-600 hover:bg-purple-700'}`}
            >
              {sendingQuestura ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Invia Tutti alla Questura
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Final Confirmation Screen
  if (showFinalConfirm) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg w-full max-w-xl my-8" data-testid="final-confirm-modal">
          <div className={`${isBookingLocked ? 'bg-green-700' : 'bg-green-600'} text-white p-6 rounded-t-lg relative`}>
            <button
              onClick={onClose}
              data-testid="close-final-confirm-btn"
              aria-label="Chiudi"
              className="absolute top-4 right-4 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
            <h2 className="text-xl font-bold flex items-center gap-2">
              {isBookingLocked ? (
                <><Lock className="h-6 w-6" /> Dati Inviati alla Questura</>
              ) : (
                <><CheckCircle2 className="h-6 w-6" /> Salva verifica ospiti</>
              )}
            </h2>
            <p className="text-green-100 mt-1">
              {isBookingLocked 
                ? 'I dati sono stati cristallizzati e non possono essere modificati'
                : 'Tutti gli ospiti sono stati verificati'
              }
            </p>
          </div>
          
          <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
            {/* Banner cristallizzazione */}
            {isBookingLocked && (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <Lock className="h-4 w-4 text-green-600 flex-shrink-0" />
                <span className="text-sm text-green-800 font-medium">Dati inviati alla Questura — non modificabili</span>
              </div>
            )}
            
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-semibold mb-3">Riepilogo Ospiti Verificati</h3>
              {guests.map((guest, idx) => {
                return (
                  <div 
                    key={guest.id} 
                    className="flex items-center gap-3 py-2 border-b last:border-0"
                    data-testid={`verified-guest-summary-${idx}`}
                  >
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <span className="font-medium">{guest.first_name} {guest.last_name}</span>
                    {guest.data_source && (
                      <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${
                        guest.data_source === 'guest_form' ? 'border-blue-300 text-blue-600' : 'border-orange-300 text-orange-600'
                      }`}>
                        {guest.data_source === 'guest_form' ? 'Ospite' : 'Responsabile'}
                      </Badge>
                    )}
                    {/* Timestamp inserimento */}
                    {guest.created_at && (
                      <span className="text-[10px] text-slate-400 whitespace-nowrap" title={`Inserito: ${new Date(guest.created_at).toLocaleString('it-IT')}`}>
                        {new Date(guest.created_at).toLocaleDateString('it-IT', {day:'2-digit', month:'2-digit'})} {new Date(guest.created_at).toLocaleTimeString('it-IT', {hour:'2-digit', minute:'2-digit'})}
                      </span>
                    )}
                    <Badge variant="outline" className="ml-auto">
                      {guest.document_type === 'passport' ? 'Passaporto' : 
                       guest.document_type === 'id_card' ? 'Carta Identità' : guest.document_type}
                    </Badge>
                    {guest.questura_sent && (
                      <Badge className="bg-green-100 text-green-700 text-xs">Inviato</Badge>
                    )}
                    {isGuestLocked(guest) && (
                      <Lock className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
            
            {!isBookingLocked && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="verification-save-explanation">
                <p className="text-sm text-green-800">
                  Questa operazione salva la verifica nel sistema. L'eventuale invio alla Questura verrà proposto separatamente dopo la chiusura.
                </p>
              </div>
            )}
          </div>
          
          <div className="p-4 border-t space-y-3">
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowFinalConfirm(false)}
                className="flex-1"
                data-testid="final-confirm-back-button"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                {isBookingLocked ? 'Chiudi' : 'Indietro'}
              </Button>
              
              {!isBookingLocked ? (
                <Button 
                  onClick={handleFinalSubmit}
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  data-testid="complete-responsible-checkin-button"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Salva ospiti inseriti
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Add Guest Form
  if (showAddGuest) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-lg w-full max-w-2xl my-8" data-testid="add-guest-modal">
          <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Aggiungi Ospite {guests.length + 1}{totalGuestsRequired > 1 ? ` di ${totalGuestsRequired}` : ''}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => setShowAddGuest(false)} className="text-white hover:bg-blue-700">
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="p-4 max-h-[70vh] overflow-y-auto space-y-4">
            {renderTurnoverToggle()}
            {/* Personal Data */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome *</Label>
                <Input 
                  value={newGuest.first_name}
                  onChange={(e) => setNewGuest({...newGuest, first_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Cognome *</Label>
                <Input 
                  value={newGuest.last_name}
                  onChange={(e) => setNewGuest({...newGuest, last_name: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sesso *</Label>
                <Select value={newGuest.gender} onValueChange={(v) => setNewGuest({...newGuest, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Maschio</SelectItem>
                    <SelectItem value="female">Femmina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo Ospite *</Label>
                <Select value={newGuest.guest_type} onValueChange={(v) => setNewGuest({...newGuest, guest_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(() => {
                      const _t = Math.max(totalGuestsRequired || 1, guests.length + 1);
                      const _o = guests.map(g => g.guest_type);
                      return (
                        <>
                          <SelectItem value="family_member" disabled={isGuestTypeDisabled('family_member', _t, _o)}>Membro Famiglia</SelectItem>
                          <SelectItem value="group_member" disabled={isGuestTypeDisabled('group_member', _t, _o)}>Membro Gruppo</SelectItem>
                          <SelectItem value="single" disabled={isGuestTypeDisabled('single', _t, _o)}>Singolo</SelectItem>
                        </>
                      );
                    })()}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Nascita *</Label>
                <Input 
                  type="date"
                  value={newGuest.birth_date}
                  onChange={(e) => setNewGuest({...newGuest, birth_date: e.target.value})}
                />
              </div>
              <div>
                <Label>Luogo Nascita *</Label>
                {(newGuest.birth_country === 'Italia' || newGuest.birth_country?.toLowerCase().includes('ital')) ? (
                  <ComuneAutocomplete
                    value={newGuest.birth_place}
                    onChange={(value) => setNewGuest({...newGuest, birth_place: value, birth_province: ''})}
                    onSelect={(comune) => {
                      setNewGuest({
                        ...newGuest, 
                        birth_place: comune.nome,
                        birth_province: comune.provincia
                      });
                    }}
                    placeholder="Cerca comune..."
                  />
                ) : (
                  <Input 
                    value={newGuest.birth_place}
                    onChange={(e) => setNewGuest({...newGuest, birth_place: e.target.value})}
                  />
                )}
              </div>
            </div>

            {(newGuest.birth_country === 'Italia' || newGuest.birth_country?.toLowerCase().includes('ital')) && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Provincia di Nascita * <span className="text-xs text-gray-400">(sigla, es. RM)</span></Label>
                  <Input
                    value={newGuest.birth_province || ''}
                    maxLength={2}
                    onChange={(e) => setNewGuest({...newGuest, birth_province: e.target.value.toUpperCase()})}
                    placeholder="RM"
                    data-testid="add-guest-birth-province-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Si compila da sola selezionando il comune dall'elenco</p>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Paese Nascita *</Label>
                <SearchableSelect 
                  options={countries}
                  value={newGuest.birth_country}
                  onValueChange={(v) => setNewGuest({...newGuest, birth_country: v})}
                  placeholder="Seleziona paese..."
                  searchPlaceholder="Cerca paese..."
                  allowCustom={true}
                />
              </div>
              <div>
                <Label>Cittadinanza *</Label>
                <SearchableSelect 
                  options={[
                    'Italiana', 'Francese', 'Tedesca', 'Spagnola', 'Britannica', 'Americana',
                    'Svizzera', 'Austriaca', 'Belga', 'Olandese', 'Portoghese', 'Polacca',
                    'Rumena', 'Greca', 'Svedese', 'Norvegese', 'Danese', 'Finlandese',
                    'Irlandese', 'Ceca', 'Ungherese', 'Croata', 'Slovena',
                    'Russa', 'Ucraina', 'Turca', 'Marocchina', 'Tunisina', 'Egiziana',
                    'Brasiliana', 'Argentina', 'Messicana', 'Canadese', 'Australiana', 
                    'Giapponese', 'Cinese', 'Indiana', 'Altra'
                  ]}
                  value={newGuest.citizenship}
                  onValueChange={(v) => setNewGuest(applyCitizenshipToGuest(newGuest, v, countries))}
                  placeholder="Seleziona cittadinanza..."
                  searchPlaceholder="Cerca cittadinanza..."
                  allowCustom={true}
                />
              </div>
            </div>
            
            <button
              type="button"
              onClick={() => setShowResidenceAdd(!showResidenceAdd)}
              className="w-full flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
              data-testid="toggle-residence-add"
            >
              <span className="font-semibold text-sm">Residenza <span className="text-xs font-normal text-slate-400">(facoltativo)</span></span>
              {showResidenceAdd ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
            </button>
            
            {showResidenceAdd && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Città Residenza <span className="text-xs text-slate-400">(facoltativo)</span></Label>
                {(newGuest.residence_country === 'Italia' || newGuest.residence_country?.toLowerCase().includes('ital')) ? (
                  <ComuneAutocomplete
                    value={newGuest.residence_city}
                    onChange={(value) => setNewGuest({...newGuest, residence_city: value})}
                    onSelect={(comune) => {
                      setNewGuest({
                        ...newGuest, 
                        residence_city: comune.nome,
                        residence_province: comune.provincia
                      });
                    }}
                    placeholder="Cerca comune..."
                  />
                ) : (
                  <Input 
                    value={newGuest.residence_city}
                    onChange={(e) => setNewGuest({...newGuest, residence_city: e.target.value})}
                  />
                )}
              </div>
              <div>
                <Label>Paese Residenza <span className="text-xs text-slate-400">(facoltativo)</span></Label>
                <SearchableSelect 
                  options={countries}
                  value={newGuest.residence_country}
                  onValueChange={(v) => setNewGuest({...newGuest, residence_country: v})}
                  placeholder="Seleziona paese..."
                  searchPlaceholder="Cerca paese..."
                  allowCustom={true}
                />
              </div>
            </div>
            )}
            
            <hr />
            
            {/* Document */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo Documento *</Label>
                <Select value={newGuest.document_type} onValueChange={(v) => setNewGuest({...newGuest, document_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="id_card">Carta Identità</SelectItem>
                    <SelectItem value="passport">Passaporto</SelectItem>
                    <SelectItem value="driving_license">Patente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Numero Documento *</Label>
                <Input 
                  value={newGuest.document_number}
                  onChange={(e) => setNewGuest({...newGuest, document_number: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Luogo Rilascio *</Label>
                <Input 
                  value={newGuest.document_issue_place}
                  onChange={(e) => setNewGuest({...newGuest, document_issue_place: e.target.value})}
                />
              </div>
              <div>
                <Label>Paese Rilascio *</Label>
                <SearchableSelect 
                  options={countries}
                  value={newGuest.document_issue_country}
                  onValueChange={(v) => setNewGuest({...newGuest, document_issue_country: v})}
                  placeholder="Seleziona paese..."
                  searchPlaceholder="Cerca paese..."
                  allowCustom={true}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Rilascio</Label>
                <Input 
                  type="date"
                  value={newGuest.document_issue_date || ''}
                  onChange={(e) => setNewGuest({...newGuest, document_issue_date: e.target.value})}
                  data-testid="add-guest-issue-date"
                />
              </div>
            </div>
          </div>

          {turnoverEnabled && (
            <div className="p-4 border-t bg-amber-50">
              <p className="text-sm font-semibold text-amber-800 mb-2">📅 Finestra di soggiorno (avvicendamento)</p>
              <p className="text-xs text-amber-700 mb-3">Di default questo ospite arriva e parte come il capogruppo. Modifica le date se <strong>entra dopo</strong> o <strong>esce prima</strong>.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data arrivo</Label>
                  <Input
                    type="date"
                    value={newGuest.arrival_date || bookingCheckIn}
                    min={bookingCheckIn}
                    max={bookingCheckOut}
                    onChange={(e) => setNewGuest({...newGuest, arrival_date: e.target.value})}
                    data-testid="add-guest-arrival-date"
                  />
                </div>
                <div>
                  <Label>Data partenza</Label>
                  <Input
                    type="date"
                    value={newGuest.departure_date || bookingCheckOut}
                    min={bookingCheckIn}
                    max={bookingCheckOut}
                    onChange={(e) => setNewGuest({...newGuest, departure_date: e.target.value})}
                    data-testid="add-guest-departure-date"
                  />
                </div>
              </div>
            </div>
          )}
          
          <div className="p-4 border-t flex gap-3">
            <Button variant="outline" onClick={() => setShowAddGuest(false)} className="flex-1">
              Annulla
            </Button>
            <Button 
              onClick={handleAddGuest}
              disabled={submitting}
              className="flex-1"
              data-testid="add-guest-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Aggiungi Ospite
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // No guests found
  if (guests.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-md" data-testid="no-guests-modal">
          <div className="bg-amber-500 text-white p-4 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Nessun Documento
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-amber-600">
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="p-6 text-center">
            <p className="text-slate-600 mb-4">
              Non ci sono documenti pre-compilati per questa prenotazione.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Questa prenotazione prevede <strong>{totalGuestsRequired} ospit{totalGuestsRequired > 1 ? 'i' : 'e'}</strong>. Inserendoli, i form si apriranno in sequenza automaticamente.
            </p>
            {renderTurnoverToggle()}
            <Button onClick={() => setShowAddGuest(true)} className="w-full" data-testid="add-guests-manually-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Aggiungi {totalGuestsRequired > 1 ? `i ${totalGuestsRequired} ospiti` : "l'ospite"}
            </Button>
          </div>
          
          <div className="p-4 border-t">
            <Button variant="outline" onClick={onClose} className="w-full">
              Chiudi
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Main Guest Verification View
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 md:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-3xl my-2 md:my-8" data-testid="guest-verification-modal">
        {sendErrorOverlay}
        {/* Header */}
        <div className="bg-blue-600 text-white p-3 md:p-4 rounded-t-lg">
          <div className="flex justify-between items-start gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="text-base md:text-lg font-bold flex items-center gap-2 truncate">
                <FileText className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
                <span className="truncate">Verifica - {booking.property_name || booking.property_code}</span>
              </h2>
              <p className="text-blue-100 text-xs md:text-sm truncate">{booking.guest_name}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-blue-700 flex-shrink-0">
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Ospite {currentGuestIndex + 1} di {guests.length}</span>
              <span>{verifiedGuests.size} verificati</span>
            </div>
            <div className="w-full bg-blue-400 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all"
                style={{ width: `${((currentGuestIndex + 1) / guests.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
        
        {/* Guest Data */}
        <div className="p-3 md:p-4 max-h-[65vh] overflow-y-auto">
          {managerCapacityAlert && (
            <div className="mb-4 p-3 border border-amber-300 bg-amber-50 text-amber-900 rounded-lg" data-testid="manager-guest-capacity-alert">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">Ospiti superiori alla prenotazione</p>
                  <p className="text-sm">Risultano {registeredPayingGuests} ospiti paganti registrati rispetto a {bookedPayingGuests} prenotati. Puoi comunque procedere; l’ufficio Prenotazioni è stato avvisato.</p>
                  {ageToVerify.length > 0 && <p className="text-xs mt-1">Età da verificare: {ageToVerify.join(', ')}.</p>}
                </div>
              </div>
            </div>
          )}
          {currentGuest && (
            <>
              {/* Guest Header */}
              <div className="flex items-center justify-between mb-3 md:mb-4 pb-3 md:pb-4 border-b gap-2">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white text-base md:text-lg font-bold flex-shrink-0 ${currentGuest.is_main_guest ? 'bg-blue-600' : 'bg-slate-500'}`}>
                    {currentGuestIndex + 1}
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-base md:text-lg truncate">{editedGuest?.first_name} {editedGuest?.last_name}</h3>
                    <div className="text-xs md:text-sm text-slate-500">
                      {currentGuest.is_main_guest && <Badge className="mr-1 md:mr-2 bg-blue-100 text-blue-700 text-xs">Principale</Badge>}
                      <span className="hidden sm:inline">
                        {editedGuest?.guest_type === 'family_head' ? 'Capofamiglia' :
                         editedGuest?.guest_type === 'family_member' ? 'Membro Famiglia' :
                         editedGuest?.guest_type === 'group_leader' ? 'Capogruppo' :
                         editedGuest?.guest_type === 'group_member' ? 'Membro Gruppo' : 'Singolo'}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Pulsanti Modifica / Elimina */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isGuestLocked(currentGuest) ? (
                    <Badge className="bg-amber-100 text-amber-700 text-xs flex items-center gap-1">
                      <Lock className="h-3 w-3" /> Inviato Questura
                    </Badge>
                  ) : (
                    <>
                      <Button 
                        variant={isEditing ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setIsEditing(!isEditing)}
                        className="text-xs md:text-sm"
                        data-testid="edit-guest-btn"
                      >
                        {isEditing ? 'Annulla' : 'Modifica'}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleDeleteGuest(currentGuest.id, `${currentGuest.first_name} ${currentGuest.last_name}`)}
                        className="text-xs md:text-sm text-red-600 hover:bg-red-50 border-red-200"
                        data-testid="delete-guest-btn"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Banner CORREZIONE GUIDATA */}
              {guidedFix && guidedFix.guestId === currentGuest?.id && (
                <div className="mb-3 p-3 rounded-lg bg-amber-50 border-2 border-amber-300" data-testid="guided-fix-banner">
                  <p className="text-sm font-semibold text-amber-900">{'\u{1F6E0}\u{FE0F}'} Correzione guidata — la Questura ha segnalato: <span className="text-red-700">{guidedFix.reason}</span></p>
                  <p className="text-sm text-amber-800 mt-1">{'\u{1F4A1}'} {guidedFix.label}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {guidedFix.value && guidedFix.field && !guidedFix.applied && (
                      <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="guided-fix-apply-btn"
                        onClick={() => {
                          handleFieldChange(guidedFix.field, guidedFix.value);
                          if (guidedFix.field === 'birth_country' && guidedFix.value !== 'Italia') handleFieldChange('birth_province', '');
                          toast.success(`${guidedFix.value} impostato — ora salva la scheda`);
                          setGuidedFix({ ...guidedFix, applied: true });
                        }}>
                        {'\u2713'} Applica: {guidedFix.value}
                      </Button>
                    )}
                    {guidedFix.applied && <span className="text-sm text-emerald-700 font-semibold self-center">{'\u2705'} Applicato — salva la scheda e reinvia</span>}
                    <Button size="sm" variant="outline" className="h-8" data-testid="guided-fix-close-btn" onClick={() => setGuidedFix(null)}>Chiudi</Button>
                  </div>
                </div>
              )}
              
              {/* Data Fields */}
              <div className="space-y-3 md:space-y-4">
                {/* Personal Info */}
                <div className="bg-slate-50 rounded-lg p-3 md:p-4">
                  <h4 className="font-semibold mb-2 md:mb-3 flex items-center gap-2 text-sm md:text-base">
                    <User className="h-4 w-4" />
                    Dati Anagrafici
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Nome *</Label>
                      {isEditing ? (
                        <Input value={editedGuest?.first_name || ''} onChange={(e) => handleFieldChange('first_name', e.target.value)} />
                      ) : (
                        <p className="font-medium">{editedGuest?.first_name || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Cognome *</Label>
                      {isEditing ? (
                        <Input value={editedGuest?.last_name || ''} onChange={(e) => handleFieldChange('last_name', e.target.value)} />
                      ) : (
                        <p className="font-medium">{editedGuest?.last_name || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Sesso *</Label>
                      {isEditing ? (
                        <Select value={editedGuest?.gender || ''} onValueChange={(v) => handleFieldChange('gender', v)}>
                          <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Maschio</SelectItem>
                            <SelectItem value="female">Femmina</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium">{editedGuest?.gender === 'male' ? 'Maschio' : editedGuest?.gender === 'female' ? 'Femmina' : '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Tipo Ospite *</Label>
                      {isEditing ? (
                        <Select value={editedGuest?.guest_type || ''} onValueChange={(v) => handleFieldChange('guest_type', v)}>
                          <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                          <SelectContent>
                            {(() => {
                              const _t = Math.max(totalGuestsRequired || 1, guests.length);
                              const _o = guests.filter(g => g.id !== editedGuest?.id).map(g => g.guest_type);
                              return (
                                <>
                                  <SelectItem value="single" disabled={isGuestTypeDisabled('single', _t, _o)}>Singolo</SelectItem>
                                  <SelectItem value="family_head" disabled={isGuestTypeDisabled('family_head', _t, _o)}>Capofamiglia</SelectItem>
                                  <SelectItem value="family_member" disabled={isGuestTypeDisabled('family_member', _t, _o)}>Membro Famiglia</SelectItem>
                                  <SelectItem value="group_leader" disabled={isGuestTypeDisabled('group_leader', _t, _o)}>Capogruppo</SelectItem>
                                  <SelectItem value="group_member" disabled={isGuestTypeDisabled('group_member', _t, _o)}>Membro Gruppo</SelectItem>
                                </>
                              );
                            })()}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium">
                          {editedGuest?.guest_type === 'family_head' ? 'Capofamiglia' :
                           editedGuest?.guest_type === 'family_member' ? 'Membro Famiglia' :
                           editedGuest?.guest_type === 'group_leader' ? 'Capogruppo' :
                           editedGuest?.guest_type === 'group_member' ? 'Membro Gruppo' : 'Singolo'}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Data Nascita *</Label>
                      {isEditing ? (
                        <Input type="date" value={formatDate(editedGuest?.birth_date)} onChange={(e) => handleFieldChange('birth_date', e.target.value)} />
                      ) : (
                        <p className="font-medium">{formatDisplayDate(editedGuest?.birth_date)}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Luogo Nascita *</Label>
                      {isEditing ? (
                        (editedGuest?.birth_country === 'Italia' || editedGuest?.birth_country?.toLowerCase().includes('ital')) ? (
                          <ComuneAutocomplete
                            value={editedGuest?.birth_place || ''}
                            onChange={(value) => setEditedGuest(prev => ({ ...prev, birth_place: value, birth_province: '' }))}
                            onSelect={(comune) => {
                              handleFieldChange('birth_place', comune.nome);
                              handleFieldChange('birth_province', comune.provincia);
                            }}
                            placeholder="Cerca comune..."
                          />
                        ) : (
                          <Input value={editedGuest?.birth_place || ''} onChange={(e) => handleFieldChange('birth_place', e.target.value)} />
                        )
                      ) : (
                        <p className="font-medium">{editedGuest?.birth_place || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Provincia Nascita {isItalianBirth(editedGuest) && '*'}</Label>
                      {!isItalianBirth(editedGuest) ? (
                        <p className="text-sm text-slate-400 italic" data-testid="birth-province-not-required">Non richiesta (nato/a all'estero)</p>
                      ) : isEditing ? (
                        <Input 
                          value={editedGuest?.birth_province || ''} 
                          onChange={(e) => handleFieldChange('birth_province', e.target.value.toUpperCase())} 
                          placeholder="es. RM, MI, GE"
                          maxLength={2}
                          className="uppercase"
                        />
                      ) : (
                        <p className="font-medium">{editedGuest?.birth_province || '-'}</p>
                      )}
                    </div>
                    <div data-guided-anchor="birth_country" className={guidedCls('birth_country')}>
                      <Label className="text-xs text-slate-500">Paese Nascita *</Label>
                      {isEditing ? (
                        <SearchableSelect 
                          options={countries}
                          value={editedGuest?.birth_country || ''}
                          onValueChange={(v) => {
                            handleFieldChange('birth_country', v);
                            if (!['italia', 'italy', 'it'].includes(String(v || '').trim().toLowerCase())) {
                              handleFieldChange('birth_province', '');
                            }
                          }}
                          placeholder="Seleziona paese..."
                          searchPlaceholder="Cerca paese..."
                          allowCustom={true}
                        />
                      ) : (
                        <p className="font-medium">{editedGuest?.birth_country || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Cittadinanza *</Label>
                      {isEditing ? (
                        <SearchableSelect 
                          options={[
                            'Italiana', 'Francese', 'Tedesca', 'Spagnola', 'Britannica', 'Americana',
                            'Svizzera', 'Austriaca', 'Belga', 'Olandese', 'Portoghese', 'Polacca',
                            'Rumena', 'Greca', 'Svedese', 'Norvegese', 'Danese', 'Finlandese',
                            'Irlandese', 'Ceca', 'Ungherese', 'Croata', 'Slovena',
                            'Russa', 'Ucraina', 'Turca', 'Marocchina', 'Tunisina', 'Egiziana',
                            'Brasiliana', 'Argentina', 'Messicana', 'Canadese', 'Australiana', 
                            'Giapponese', 'Cinese', 'Indiana', 'Altra'
                          ]}
                          value={editedGuest?.citizenship || ''}
                          onValueChange={(v) => handleFieldChange('citizenship', v)}
                          placeholder="Seleziona cittadinanza..."
                          searchPlaceholder="Cerca cittadinanza..."
                          allowCustom={true}
                        />
                      ) : (
                        <p className="font-medium">{editedGuest?.citizenship || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Codice Fiscale {editedGuest?.citizenship === 'Italiana' && '(consigliato)'}</Label>
                      {isEditing ? (
                        <Input value={editedGuest?.codice_fiscale || ''} onChange={(e) => handleFieldChange('codice_fiscale', e.target.value.toUpperCase())} className="uppercase" maxLength={16} />
                      ) : (
                        <p className="font-medium font-mono">{editedGuest?.codice_fiscale || '-'}</p>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Residence - Collapsible */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <button
                    type="button"
                    onClick={() => setShowResidenceEdit(!showResidenceEdit)}
                    className="w-full flex items-center justify-between mb-1"
                    data-testid="toggle-residence-edit"
                  >
                    <h4 className="font-semibold">Residenza <span className="text-xs font-normal text-slate-400">(facoltativo)</span></h4>
                    {showResidenceEdit ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  </button>
                  {showResidenceEdit && (
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label className="text-xs text-slate-500">Indirizzo <span className="text-xs text-slate-400">(facoltativo)</span></Label>
                      {isEditing ? (
                        <Input value={editedGuest?.residence_address || ''} onChange={(e) => handleFieldChange('residence_address', e.target.value)} />
                      ) : (
                        <p className="font-medium">{editedGuest?.residence_address || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Città <span className="text-xs text-slate-400">(facoltativo)</span></Label>
                      {isEditing ? (
                        (editedGuest?.residence_country === 'Italia' || editedGuest?.residence_country?.toLowerCase().includes('ital')) ? (
                          <ComuneAutocomplete
                            value={editedGuest?.residence_city || ''}
                            onChange={(value) => handleFieldChange('residence_city', value)}
                            onSelect={(comune) => {
                              handleFieldChange('residence_city', comune.nome);
                              handleFieldChange('residence_province', comune.provincia);
                            }}
                            placeholder="Cerca comune..."
                          />
                        ) : (
                          <Input value={editedGuest?.residence_city || ''} onChange={(e) => handleFieldChange('residence_city', e.target.value)} />
                        )
                      ) : (
                        <p className="font-medium">{editedGuest?.residence_city || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Provincia <span className="text-xs text-slate-400">(facoltativo)</span></Label>
                      {isEditing ? (
                        <Input 
                          value={editedGuest?.residence_province || ''} 
                          onChange={(e) => handleFieldChange('residence_province', e.target.value.toUpperCase())} 
                          placeholder="es. RM, MI, GE"
                          maxLength={2}
                          className="uppercase"
                        />
                      ) : (
                        <p className="font-medium">{editedGuest?.residence_province || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">CAP <span className="text-xs text-slate-400">(facoltativo)</span></Label>
                      {isEditing ? (
                        <Input 
                          value={editedGuest?.residence_postal_code || ''} 
                          onChange={(e) => handleFieldChange('residence_postal_code', e.target.value)} 
                          maxLength={5}
                        />
                      ) : (
                        <p className="font-medium">{editedGuest?.residence_postal_code || '-'}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-slate-500">Paese <span className="text-xs text-slate-400">(facoltativo)</span></Label>
                      {isEditing ? (
                        <SearchableSelect 
                          options={countries}
                          value={editedGuest?.residence_country || ''}
                          onValueChange={(v) => handleFieldChange('residence_country', v)}
                          placeholder="Seleziona paese..."
                          searchPlaceholder="Cerca paese..."
                          allowCustom={true}
                        />
                      ) : (
                        <p className="font-medium">{editedGuest?.residence_country || '-'}</p>
                      )}
                    </div>
                  </div>
                  )}
                </div>
                
                {/* Document */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Documento
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Tipo Documento{isDocumentRequired(editedGuest) && <span className="text-red-500"> *</span>}</Label>
                      {isEditing ? (
                        <Select value={editedGuest?.document_type || ''} onValueChange={(v) => handleFieldChange('document_type', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="id_card">Carta Identità</SelectItem>
                            <SelectItem value="passport">Passaporto</SelectItem>
                            <SelectItem value="driving_license">Patente</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium">
                          {editedGuest?.document_type === 'passport' ? 'Passaporto' : 
                           editedGuest?.document_type === 'id_card' ? 'Carta Identità' :
                           editedGuest?.document_type === 'driving_license' ? 'Patente' : editedGuest?.document_type || '-'}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Numero{isDocumentRequired(editedGuest) && <span className="text-red-500"> *</span>}</Label>
                      {isEditing ? (
                        <Input value={editedGuest?.document_number || ''} onChange={(e) => handleFieldChange('document_number', e.target.value)} />
                      ) : (
                        <p className="font-medium font-mono">{editedGuest?.document_number || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Luogo Rilascio{isDocumentRequired(editedGuest) && <span className="text-red-500"> *</span>}</Label>
                      {isEditing ? (
                        <Input value={editedGuest?.document_issue_place || ''} onChange={(e) => handleFieldChange('document_issue_place', e.target.value)} />
                      ) : (
                        <p className="font-medium">{editedGuest?.document_issue_place || '-'}</p>
                      )}
                    </div>
                    <div data-guided-anchor="document_issue_country" className={guidedCls('document_issue_country')}>
                      <Label className="text-xs text-slate-500">Paese Rilascio{isDocumentRequired(editedGuest) && <span className="text-red-500"> *</span>}</Label>
                      {isEditing ? (
                        <SearchableSelect 
                          options={countries}
                          value={editedGuest?.document_issue_country || ''}
                          onValueChange={(v) => handleFieldChange('document_issue_country', v)}
                          placeholder="Seleziona paese..."
                          searchPlaceholder="Cerca paese..."
                          allowCustom={true}
                        />
                      ) : (
                        <p className="font-medium">{editedGuest?.document_issue_country || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Data Rilascio</Label>
                      {isEditing ? (
                        <Input type="date" value={formatDate(editedGuest?.document_issue_date)} onChange={(e) => handleFieldChange('document_issue_date', e.target.value)} />
                      ) : (
                        <p className="font-medium">{formatDisplayDate(editedGuest?.document_issue_date)}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Scadenza</Label>
                      {isEditing ? (
                        <Input type="date" value={formatDate(editedGuest?.document_expiry_date)} onChange={(e) => handleFieldChange('document_expiry_date', e.target.value)} />
                      ) : (
                        <p className="font-medium">{formatDisplayDate(editedGuest?.document_expiry_date)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {turnoverEnabled && (
                <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200" data-testid="edit-guest-turnover-dates">
                  <p className="text-sm font-semibold text-amber-800 mb-2">📅 Finestra di soggiorno (avvicendamento)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-slate-500">Data arrivo</Label>
                      {isEditing ? (
                        <Input type="date" value={formatDate(editedGuest?.arrival_date) || bookingCheckIn} min={bookingCheckIn} max={bookingCheckOut} onChange={(e) => handleFieldChange('arrival_date', e.target.value)} data-testid="edit-guest-arrival-date" />
                      ) : (
                        <p className="font-medium">{editedGuest?.arrival_date ? formatDisplayDate(editedGuest.arrival_date) : `${formatDisplayDate(bookingCheckIn)} (da prenotazione)`}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Data partenza</Label>
                      {isEditing ? (
                        <Input type="date" value={formatDate(editedGuest?.departure_date) || bookingCheckOut} min={bookingCheckIn} max={bookingCheckOut} onChange={(e) => handleFieldChange('departure_date', e.target.value)} data-testid="edit-guest-departure-date" />
                      ) : (
                        <p className="font-medium">{editedGuest?.departure_date ? formatDisplayDate(editedGuest.departure_date) : `${formatDisplayDate(bookingCheckOut)} (da prenotazione)`}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Footer Actions */}
        <div className="p-3 md:p-4 border-t bg-slate-50 rounded-b-lg">
          {/* Guest Counter & Children - with edit capability */}
          <div className="flex flex-wrap items-center gap-2 mb-3 pb-3 border-b">
            {/* Editable Guests Count */}
            <div className="flex items-center gap-2">
              <Label className="text-xs md:text-sm text-slate-600">Ospiti totali:</Label>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpdateGuestsCount(Math.max(1, totalGuestsRequired - 1))}
                  disabled={isBookingLocked || totalGuestsRequired <= 1}
                  className="h-7 w-7 p-0"
                >
                  -
                </Button>
                <span className="w-8 text-center font-bold">{totalGuestsRequired}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUpdateGuestsCount(totalGuestsRequired + 1)}
                  disabled={isBookingLocked}
                  className="h-7 w-7 p-0"
                >
                  +
                </Button>
              </div>
              <span className="text-slate-300">|</span>
              <span className="text-xs md:text-sm text-slate-600">
                Documenti: <strong className={documentsCount > 0 ? 'text-green-600' : 'text-red-600'}>{documentsCount}</strong>
              </span>
            </div>
          </div>

          {renderTurnoverToggle()}

          {/* Status Badge */}
          <div className="mb-3">
            {allGuestsAccounted ? (
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Tutti gli ospiti registrati ({documentsCount}/{totalGuestsRequired})
              </Badge>
            ) : (
              <Badge className="bg-orange-100 text-orange-700">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Mancano {totalGuestsRequired - documentsCount} ospiti ({documentsCount}/{totalGuestsRequired})
              </Badge>
            )}
          </div>

          <div className="flex flex-wrap gap-2 md:gap-3">
            {/* Navigation */}
            <Button 
              variant="outline" 
              onClick={handlePrevGuest}
              disabled={currentGuestIndex === 0}
              size="sm"
              className="text-xs md:text-sm"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Precedente</span>
            </Button>
            
            {/* Add Guest Button - nascosto se già inviato alla Questura */}
            {!booking.questura_sent && (
              <Button 
                variant="outline"
                onClick={() => setShowAddGuest(true)}
                size="sm"
                className="text-xs md:text-sm"
                data-testid="manager-add-extra-guest-button"
              >
                <UserPlus className="h-4 w-4" />
                <span className="hidden sm:inline ml-1">Aggiungi</span>
              </Button>
            )}
            
            <div className="flex-1" />
            
            {/* Save if editing - nascosto se cristallizzato */}
            {isEditing && !isBookingLocked && (
              <Button onClick={handleSaveGuest} disabled={submitting} size="sm" className="text-xs md:text-sm" data-testid="save-guest-btn">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                Salva
              </Button>
            )}
            
            {/* Badge se già inviato */}
            {booking.questura_sent && (
              <Badge className="bg-green-100 text-green-700 h-8 flex items-center">
                <CheckCircle2 className="h-4 w-4 mr-1" />
                Inviato alla Questura
              </Badge>
            )}
            
            {/* Confirm / Next */}
            {!isEditing && (
              <Button 
                onClick={handleConfirmGuest}
                className="bg-green-600 hover:bg-green-700 text-xs md:text-sm"
                size="sm"
                data-testid="confirm-next-guest-btn"
              >
                <Check className="h-4 w-4 mr-1" />
                {currentGuestIndex < guests.length - 1 ? (
                  <>
                    <span className="hidden sm:inline">Conferma e </span>Prossimo
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Conferma e </span>Completa
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestVerificationModal;
